/**
 * AR Playbook Admin API
 *
 * API endpoints for BUSINESS_USER (admin/regular) functionality in AR Playbook
 * - View own licenses
 * - Business user analytics (own data only)
 *
 * For SUPER_ADMIN functionality, see arPlaybookSuperAdminApi.ts
 */

import { createApi } from '@reduxjs/toolkit/query/react'

import { host } from '../constants/apiconfig'
import { businessUserBaseQueryWithAuth } from './arPlaybookBaseQuery'

// ============================================
// Types
// ============================================

export interface ILicenseAdmin {
    _id: string
    licenseKey: string
    licenseNo: number
    status: 'unassigned' | 'pending' | 'active' | 'expired'
    maxDevices: number
    devices: string[]
    creationDate: string
    activationDate?: string
    expiryDate?: string
    book?: {
        _id: string
        name: string
    }
    business?: {
        _id: string
        name: string
        institutionName: string
    }
    student?: {
        _id: string
        studentName: string
    }
}

export interface IPagination {
    total: number
    page: number
    limit: number
    totalPages: number
}

export interface IGetLicensesResponse {
    licenses: ILicenseAdmin[]
    pagination: IPagination
}

export interface IGetLicensesArgs {
    page?: number
    limit?: number
    status?: 'unassigned' | 'pending' | 'active' | 'expired'
    bookId?: string
}

// ============================================
// API Definition
// ============================================

export const arPlaybookAdminApi = createApi({
    reducerPath: 'arPlaybookAdminApi',
    baseQuery: businessUserBaseQueryWithAuth,
    tagTypes: ['BusinessUserLicenses'],
    endpoints: builder => ({
        // ============================================
        // License Management (Business User - Own Licenses Only)
        // ============================================

        getBusinessUserLicenses: builder.query<IGetLicensesResponse, IGetLicensesArgs>({
            query: ({ page = 1, limit = 20, status, bookId }) => ({
                url: `${host}ar-playbook/business-users/licenses`,
                params: {
                    page,
                    limit,
                    ...(status && { status }),
                    ...(bookId && { bookId }),
                },
            }),
            providesTags: ['BusinessUserLicenses'],
        }),
    }),
})

// Export hooks for Business User
export const { useGetBusinessUserLicensesQuery } = arPlaybookAdminApi
